import { Checkbox } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { ColumnsPicker, EnumSelect, ListHeaderFields, SelectionFields } from '../BlockFields.js';
import { useBlockOptions } from '../useBlockOptions.js';
import type { BlockFormProps } from './types.js';

/**
 * Ro'yxat bloklari — 7 ta.
 *
 * Hammasi bir xil qismdan iborat: sarlavha (`ListHeaderFields`) va nima
 * ko'rsatilishi (`SelectionFields`). Farq faqat ko'rinish sozlamalarida
 * va qaysi kolleksiyadan tanlashda.
 */

// ─── 16. Yangiliklar ─────────────────────────────────────────────────────────

export function NewsListForm({ data, onChange }: BlockFormProps<'news-list'>) {
  const categories = useBlockOptions('news-categories', data.selection.mode === 'category');
  const news = useBlockOptions('news', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        modes={['auto', 'featured', 'category', 'manual']}
        targets={categories.options}
        targetLabel="Yangilik kategoriyasi"
        items={news.options}
        itemsLabel="Yangiliklar"
      />
      <EnumSelect
        label="Ko‘rinishi"
        value={data.layout}
        options={[
          { value: 'grid', label: 'Kartochkalar' },
          { value: 'list', label: 'Ro‘yxat' },
        ]}
        onChange={(layout) => onChange({ ...data, layout })}
      />
      {data.layout === 'grid' && (
        <ColumnsPicker
          value={data.columns}
          options={[2, 3, 4]}
          onChange={(columns) => onChange({ ...data, columns: columns as 2 | 3 | 4 })}
        />
      )}
    </>
  );
}

// ─── 17. Xizmatlar ───────────────────────────────────────────────────────────

export function ServicesListForm({ data, onChange }: BlockFormProps<'services-list'>) {
  const services = useBlockOptions('services', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        items={services.options}
        itemsLabel="Xizmatlar"
      />
      <EnumSelect
        label="Qaysi xizmatlar"
        value={data.serviceType}
        options={[
          { value: 'all', label: 'Hammasi' },
          { value: 'main', label: 'Asosiy xizmatlar' },
          { value: 'repair', label: 'Ta’mirlash va texnik xizmat' },
        ]}
        onChange={(serviceType) => onChange({ ...data, serviceType })}
      />
      <EnumSelect
        label="Ko‘rinishi"
        value={data.layout}
        options={[
          { value: 'grid', label: 'Kartochkalar' },
          { value: 'list', label: 'Ro‘yxat' },
        ]}
        onChange={(layout) => onChange({ ...data, layout })}
      />
      {data.layout === 'grid' && (
        <ColumnsPicker
          value={data.columns}
          options={[2, 3, 4]}
          onChange={(columns) => onChange({ ...data, columns: columns as 2 | 3 | 4 })}
        />
      )}
    </>
  );
}

// ─── 18. Loyihalar ───────────────────────────────────────────────────────────

export function ProjectsListForm({ data, onChange }: BlockFormProps<'projects-list'>) {
  const projects = useBlockOptions('projects', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        items={projects.options}
        itemsLabel="Loyihalar"
      />
      <Checkbox
        label="Xizmat bo‘yicha filtr ko‘rsatilsin"
        checked={data.showFilter}
        onChange={(event) => onChange({ ...data, showFilter: event.target.checked })}
      />
      <ColumnsPicker
        value={data.columns}
        options={[2, 3]}
        onChange={(columns) => onChange({ ...data, columns: columns as 2 | 3 })}
      />
    </>
  );
}

// ─── 19. Rahbariyat ──────────────────────────────────────────────────────────

export function StaffListForm({ data, onChange }: BlockFormProps<'staff-list'>) {
  const staff = useBlockOptions('staff', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        items={staff.options}
        itemsLabel="Rahbarlar"
      />
      <EnumSelect
        label="Ko‘rinishi"
        value={data.layout}
        options={[
          { value: 'grid', label: 'Kartochkalar' },
          { value: 'list', label: 'Ro‘yxat' },
        ]}
        onChange={(layout) => onChange({ ...data, layout })}
      />
    </>
  );
}

// ─── 20. Hujjatlar ───────────────────────────────────────────────────────────

export function DocumentsListForm({ data, onChange }: BlockFormProps<'documents-list'>) {
  const categories = useBlockOptions('document-categories', data.selection.mode === 'category');
  const documents = useBlockOptions('documents', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        modes={['auto', 'category', 'manual']}
        targets={categories.options}
        targetLabel="Hujjat kategoriyasi"
        items={documents.options}
        itemsLabel="Hujjatlar"
      />
      <Checkbox
        label="Qidiruv maydoni"
        checked={data.showSearch}
        onChange={(event) => onChange({ ...data, showSearch: event.target.checked })}
      />
      <Checkbox
        label="Kategoriya bo‘yicha filtr"
        checked={data.showCategoryFilter}
        onChange={(event) => onChange({ ...data, showCategoryFilter: event.target.checked })}
      />
    </>
  );
}

// ─── 28. Kurslar ─────────────────────────────────────────────────────────────

export function CoursesListForm({ data, onChange }: BlockFormProps<'courses-list'>) {
  const courses = useBlockOptions('courses', data.selection.mode === 'manual');

  return (
    <>
      <ListHeaderFields value={data} onChange={(patch) => onChange({ ...data, ...patch })} />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        items={courses.options}
        itemsLabel="Kurslar"
      />
      <Checkbox
        label="Guruhlar jadvali ko‘rsatilsin"
        checked={data.showSchedule}
        onChange={(event) => onChange({ ...data, showSchedule: event.target.checked })}
      />
    </>
  );
}

// ─── 29. Xizmat narxlari ─────────────────────────────────────────────────────

export function ServicesPriceForm({ data, onChange }: BlockFormProps<'services-price'>) {
  const services = useBlockOptions('services', data.selection.mode === 'manual');

  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <SelectionFields
        value={data.selection}
        onChange={(selection) => onChange({ ...data, selection })}
        items={services.options}
        itemsLabel="Xizmatlar"
      />
      <Checkbox
        label="Narxlar ko‘rsatilsin"
        checked={data.showPrices}
        onChange={(event) => onChange({ ...data, showPrices: event.target.checked })}
      />
      <TranslatableInput
        label="Izoh"
        multiline
        rows={2}
        value={data.note}
        onChange={(note) => onChange({ ...data, note })}
        hint="Jadval ostida chiqadi — «Narxlar taxminiy» kabi"
      />
    </>
  );
}
